import type { DropRect } from "./dock-drop.mjs";

/**
 * drop-targets — a tiny registry so a drag can find things it might land on
 * without knowing what they are.
 *
 * The desktop grid owns the drag; the dock lives in DesktopChrome. Without
 * this, the grid would have to `querySelector(".dock")` from inside its drag
 * maths — which hardcodes another component's markup into the geometry layer
 * and cannot be reasoned about when the dock is absent (phone mode). Instead
 * the dock REGISTERS itself, the grid asks the registry for a rect, and
 * neither imports the other.
 *
 * The registry doubles as the hover channel in the other direction: the grid
 * publishes "the pointer is over you, and I would/wouldn't be accepted" and
 * the target renders its own affordance. That keeps the highlight's styling
 * with the component that owns it.
 */

export type DropTargetId = "dock";

export type DropItemRect = { left: number; right: number };

export type DropTargetSource = {
  /** Live viewport rect. Return null when the target is not on screen. */
  rect: () => DropRect | null;
  /**
   * Rects of the target's REORDERABLE children, for an insertion caret.
   *
   * Only the children a drop can actually land between. The dock also renders
   * a fixed Applications button; counting it would let dockInsertIndex return
   * an index one past the last pin — a caret position no reorder can honour.
   */
  itemRects?: () => DropItemRect[];
};

export type DropHover = {
  targetId: DropTargetId;
  /** Insertion position within the target (see dockInsertIndex). */
  index: number;
  /** False while the target would REFUSE the payload — still hovered, still highlighted, differently. */
  accepted: boolean;
};

class DropTargetRegistry {
  private sources = new Map<DropTargetId, DropTargetSource>();
  private listeners = new Set<() => void>();

  /** Currently hovered target during a drag, or null when nothing is hovered. */
  hover: DropHover | null = null;

  /** Returns an unregister function, so callers can hand it straight to useEffect. */
  register(id: DropTargetId, source: DropTargetSource): () => void {
    this.sources.set(id, source);
    return () => {
      // Only drop OUR source: a remount can register the replacement before
      // the old effect cleans up, and blindly deleting would unregister the
      // live one and silently kill the drop target.
      if (this.sources.get(id) === source) this.sources.delete(id);
      if (this.hover?.targetId === id) this.setHover(null);
    };
  }

  rect(id: DropTargetId): DropRect | null {
    const r = this.sources.get(id)?.rect();
    return r || null;
  }

  itemRects(id: DropTargetId): DropItemRect[] {
    return this.sources.get(id)?.itemRects?.() || [];
  }

  /** Idempotent: an unchanged hover does not re-render the target on every pointermove. */
  setHover(next: DropHover | null) {
    const a = this.hover;
    if (a === next) return;
    if (
      a &&
      next &&
      a.targetId === next.targetId &&
      a.index === next.index &&
      a.accepted === next.accepted
    ) {
      return;
    }
    this.hover = next;
    for (const fn of this.listeners) fn();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
}

export const dropTargets = new DropTargetRegistry();
